import { useMemo, useRef } from 'react'
import {
  currentTaskFromRunning,
  deriveInitialTestRunPhase,
  explorationHost,
  findInitialTestSession,
  pickExplorationReplayId,
  shouldShowInitialTestRunInterim,
  type InitialTestRunPhase,
} from '../lib/initialTestRun'
import type { Exploration, Journey, Project, Task, TestSession } from '../data/projectTypes'

export interface InitialTestRunInputs {
  testSessions: TestSession[]
  testSessionsProjectId: string | undefined
  testSessionsLoading: boolean
  explorations: Exploration[]
  expTotal: number
  explorationsProjectId: string | undefined
  explorationsFetchPending: boolean
  journeys: Journey[]
  journeyTotal: number
  journeysProjectId: string | undefined
  journeysFetchPending: boolean
  journeyStatusCounts: Record<string, number>
  bugTotalCount: number
  bugsProjectId: string | undefined
  bugCountPending: boolean
  running: Task[]
  project: Project | undefined
}

export function useInitialTestRun(projectId: string | undefined, inputs: InitialTestRunInputs) {
  // The page owns these RTK Query subscriptions so the interim-state calculation cannot create a
  // second full-history request beside the visible project lists.
  const {
    testSessions,
    testSessionsProjectId,
    testSessionsLoading,
    explorations,
    expTotal,
    explorationsProjectId,
    explorationsFetchPending,
    journeys,
    journeyTotal,
    journeysProjectId,
    journeysFetchPending,
    journeyStatusCounts,
    bugTotalCount,
    bugsProjectId,
    bugCountPending,
    running,
    project,
  } = inputs

  // While a project's data is loading for the FIRST time we can't yet tell whether to show the interim
  // setup screen or the regular overview — deciding too early flashes the wrong screen. The four signals
  // that drive that decision are the initial test session, the exploration list, the bug count, and the
  // journey list.
  //
  // Each signal latches independently the first time its fetch returns for THIS project. We deliberately
  // do NOT require them to be idle in the same render: a project mid-run polls all of these on a timer,
  // so their pending flags are constantly staggered and would (almost) never all be idle simultaneously
  // — which previously left the loading screen stuck for the whole exploration and swallowed the interim
  // view. Independent latches clear the gate as soon as each signal has produced a result once, and stay
  // latched through later polls. They reset when the project changes.
  const loadedRef = useRef<{
    projectId: string | null
    testSessions: boolean
    explorations: boolean
    bugs: boolean
    journeys: boolean
  }>({ projectId: null, testSessions: false, explorations: false, bugs: false, journeys: false })

  if (loadedRef.current.projectId !== (projectId ?? null)) {
    loadedRef.current = {
      projectId: projectId ?? null,
      testSessions: false,
      explorations: false,
      bugs: false,
      journeys: false,
    }
  }
  if (projectId) {
    if (testSessionsProjectId === projectId && !testSessionsLoading) loadedRef.current.testSessions = true
    if (explorationsProjectId === projectId && !explorationsFetchPending) loadedRef.current.explorations = true
    if (bugsProjectId === projectId && !bugCountPending) loadedRef.current.bugs = true
    if (journeysProjectId === projectId && !journeysFetchPending) loadedRef.current.journeys = true
  }

  const decisionReady =
    !projectId ||
    (loadedRef.current.testSessions &&
      loadedRef.current.explorations &&
      loadedRef.current.journeys &&
      loadedRef.current.bugs)

  const derived = useMemo(() => {
    const empty = {
      showInterim: false,
      initialSession: null as TestSession | null,
      phase: 'done' as InitialTestRunPhase,
      explorationId: null as string | null,
      currentTask: null as { title: string; description: string } | null,
      journeyCount: 0,
      explorationCount: 0,
      host: 'your app',
      targetUrl: null as string | null,
      projectName: null as string | null,
    }
    if (!projectId) return empty

    const sessionsForProject = testSessionsProjectId === projectId ? testSessions : []
    const initialSession = findInitialTestSession(sessionsForProject)

    const expInProgress = explorations.filter((e) => e.status === 'in-progress').length
    const expCompleted = explorations.filter((e) => e.status === 'completed').length
    const expFailed = explorations.filter((e) => e.status === 'failed').length

    const sc = journeyStatusCounts
    const hasStatusCounts = Object.keys(sc).length > 0
    const journeyInProgress = hasStatusCounts
      ? (sc['in-progress'] ?? 0)
      : journeys.filter((j) => j.last_run_status === 'in-progress').length
    const journeyQueued = hasStatusCounts
      ? (sc['queued'] ?? 0)
      : journeys.filter((j) => j.last_run_status === 'queued').length
    const journeyPending = journeyInProgress + journeyQueued

    const phase = deriveInitialTestRunPhase({
      expInProgress,
      expCompleted,
      expFailed,
      expTotal,
      journeyPending,
      journeyTotal,
    })

    // Only trust bug/journey counts once they belong to the current project — the slices reset these
    // to 0 while the next project's data is loading, so a stale count can't prematurely hide the interim.
    const hasJourneys = journeysProjectId === projectId && journeyTotal > 0
    const hasBugs = bugsProjectId === projectId && bugTotalCount > 0

    const showInterim = shouldShowInitialTestRunInterim({
      initialSession,
      expTotal,
      expCompleted,
      expFailed,
      hasBugs,
      hasJourneys,
    })

    const projectRunning = running.filter((t) => t.project_id === projectId)
    const hostProject = project?.id === projectId ? project : null

    return {
      showInterim,
      initialSession,
      phase: showInterim ? phase : ('done' as InitialTestRunPhase),
      // Prefer the id embedded in the initial session's own row: the test-sessions response already
      // carries it (QA bug bug-mtz78xvj-u8re), so the replay's /api/task-events request can start as
      // soon as that response lands instead of also waiting on the separate /api/explorations response.
      explorationId: pickExplorationReplayId(initialSession?.explorations ?? []) ?? pickExplorationReplayId(explorations),
      currentTask: currentTaskFromRunning(projectRunning),
      journeyCount: journeyTotal,
      explorationCount: expTotal,
      host: explorationHost(hostProject?.target_url, hostProject?.name),
      targetUrl: hostProject?.target_url ?? null,
      projectName: hostProject?.name ?? null,
    }
  }, [
    projectId,
    testSessions,
    testSessionsProjectId,
    explorations,
    expTotal,
    journeys,
    journeyTotal,
    journeysProjectId,
    journeyStatusCounts,
    bugTotalCount,
    bugsProjectId,
    running,
    project,
  ])

  // Consumers render their normal tab skeletons while these signals arrive. Keep the special first-run
  // view hidden until its inputs are conclusive instead of exposing a page-wide "resolving" gate.
  return { ...derived, showInterim: decisionReady && derived.showInterim }
}
